/** Global game constants. All units are pixels / seconds / degrees unless noted. */

export const PROTOCOL_VERSION = 1;

export const TICK_RATE = 60;
export const TICK_DT = 1 / TICK_RATE;

export const WORLD_W = 2560;
export const WORLD_H = 1280;

export const GRAVITY = 900;

/**
 * Character jumps float on lighter gravity than projectiles — cartoon
 * hang-time that still lands where the solver aimed. The solver, the sim
 * (`stepScript`) and the client preview must all use this same scale or the
 * dotted arc and the arc you fly drift apart.
 */
export const JUMP_GRAVITY_SCALE = 0.62;

/** Wind is horizontal acceleration applied to projectiles (and lightly to characters). */
export const WIND_MAX = 35;

export const TURN_TIME_MS = 30_000;
export const TURN_TIME_FAST_MS = 20_000;

/**
 * Bullet-time window: how long (in sim ticks) the world crawls after the
 * airborne player engages it by dragging. At SLOWMO_RATE the host advances
 * the sim once per N render frames, so 90 ticks of sim ≈ 1.5 s of wall-clock
 * — the client heartbeats a re-arm every couple of seconds while the drag
 * is held, so the window only ends when they release (or land).
 */
export const SLOWMO_TICKS = 90;
export const SLOWMO_RATE = 4;

/** Mid-air shots allowed per jump arc — one pull, one shot. */
export const MID_AIR_SHOTS = 1;

/** HP a burning character coughs up when its skipped turn comes around. */
export const BURN_TURN_DMG = 5;

/**
 * Pigeon: seconds of straight, cannon-aimed flight before the bird "wakes
 * up" and starts maneuvering toward its mark.
 */
export const HOMING_ARM_S = 0.6;

/** Landing faster than this (px/s) starts causing fall damage. */
export const FALL_DMG_THRESHOLD = 550;

/** Seconds overlapping water before a character drowns. */
export const DROWN_TIME_S = 3;

/** Explosion damage below this is rounded away. */
export const MIN_EXPLOSION_DMG = 5;

/** Characters come to rest below this speed. */
export const REST_SPEED = 12;

/** Settle phase cap once all projectiles are gone. */
export const SETTLE_TIMEOUT_S = 3;

export const MAX_HP = 100;

/** Sub-intents a player may issue during one aiming phase. */
export const ALLOWED_SUB_INTENTS = ['jump', 'fire'] as const;

/** Dialogue pacing (deterministic, stored in GameState). */
export const DIALOGUE_COOLDOWN_S = 6;
export const DIALOGUE_BEAT_S = 1.1;

export const SWAPS_PER_MATCH = 1;

export const PHASE_LABELS = {
  lobby: 'lobby',
  turn_start: 'turn_start',
  aiming: 'aiming',
  resolving: 'resolving',
  settle: 'settle',
  dialogue: 'dialogue',
  match_end: 'match_end',
} as const;
